import { useEffect, useState } from "react";
import { PHOTO_PICKER_MODEL } from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import { getDeletedFileIds, MANAGE_RESERVATION_MODEL } from "../../Events/Manage/Model/Model";
import HandleChange from "../../../../Helpers/HandleChange";
import {getStatusNameById, STATUS_MODEL} from "../../../../enums/statusEnum";
import {useDispatch, useSelector} from "react-redux";
import {
    CreateFileEventSetting,
    DeleteFileEventSetting,
    GetEventSetting,
    UpdateEventSetting
} from "../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi";
import { errorMsg } from "../../../../Apis/functions";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {permissionObj} from "../../../../Helpers/permission";

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */


const ManageApp = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const disabled = !DEV && !permission?.includes(permissionObj?.WEB_EVENT_MANAGE_UPDATE);
    const [item, setItem] = useState({ ...MANAGE_RESERVATION_MODEL })
    const [photoPickerFile, setPhotoPickerFile] = useState({ ...PHOTO_PICKER_MODEL });
    const [emptyFields, setEmptyFields] = useState(false)
    const [modified, setModified] = useState(false)

    const { handleChange: baseHandleChange } = HandleChange({ model: item, setModel: setItem });
    const isBookingWindowEnabled = item.bookingWindowEnabled === true;
    const isAttendeesLimitEnabled = item.attendeesLimitsEnabled === true;

    const handleChange = (evt, name, eventTypeValue, mediaOptionId) => {
        // Run the base form handleChange logic first
        baseHandleChange(evt, name);
        setEmptyFields(false);
        const fieldName = name || evt?.target?.name;
        const checked = evt?.target?.checked;



        /**
         *  Handle Notification Channels Table checkboxes
         * (Executes only if eventTypeValue and mediaOptionId are provided)
         */
        if (eventTypeValue && mediaOptionId) {
            setItem((prev) => {
                const current = [...(prev.eventSettingMediaOptionDto || [])];

                // Locate record matching this eventType + mediaOption
                const index = current.findIndex(
                    (ch) =>
                        ch.eventType === eventTypeValue &&
                        ch.mediaOption === mediaOptionId
                );

                if (index >= 0) {
                    // Update status for existing channel
                    current[index] = {
                        ...current[index],
                        status: {
                            id: checked
                                ? STATUS_MODEL.ACTIVE_COMPANY // 11 → Active
                                : STATUS_MODEL.IN_ACTIVE_COMPANY, // 12 → Inactive
                            name: checked
                                ? getStatusNameById(STATUS_MODEL.ACTIVE_COMPANY)
                                : getStatusNameById(STATUS_MODEL.IN_ACTIVE_COMPANY)
                        },
                    };
                } else if (checked) {
                    // ➕ Add new channel entry if not found and checked
                    current.push({
                        id: null,
                        eventType: eventTypeValue,
                        mediaOption: mediaOptionId,
                        eventSettingId: prev.id,
                        status: {
                            id: STATUS_MODEL.ACTIVE_COMPANY,
                            name: getStatusNameById(STATUS_MODEL.ACTIVE_COMPANY)
                        },
                    });
                }

                return {
                    ...prev,
                    eventSettingMediaOptionDto: current,
                };
            });
            return; // Exit early if handled
        }

        /**
         * Handle other toggle/switch fields (boolean-like values)
         */
        switch (fieldName) {
            case 'attendeesLimitsEnabled':
            case 'bookingWindowEnabled':
                setItem((prev) => ({
                    ...prev,
                    [fieldName]: checked !== undefined ? checked : !prev[fieldName],
                }));
                break;
            default:
                break;
        }
    };

    const handleUploadFile = async (eventSettingId, uploadFile) => {
        const formData = new FormData();

        if (Array.isArray(uploadFile)) {
            uploadFile.forEach((file) => {
                formData.append('fileUploadDto.files', file);
            });
        } else if (uploadFile) {
            formData.append('fileUploadDto.files', uploadFile);
        }

        formData.append('eventSettingId', eventSettingId);
        formData.append('fileUploadDto.userFileNames', uploadFile?.name || '');

        return dispatch(CreateFileEventSetting(formData));
    };

    const createMultiple = async (eventSettingId) => {
        let files = photoPickerFile.listFiles?.filter((e)=> e.id === null);

        if (!files || files.length === 0) return;

        await Promise.all(
            files.map((item) => handleUploadFile(eventSettingId, item.fileSelected))
        );
    };

    useEffect(() => {
        if (emptyFields && item.isValid()) {
            setEmptyFields(false);
        }
    }, [item]);

    const handleUpdate = async () => {

        if (!item.isValid()) {
            setEmptyFields(true)
            return
        }

        setEmptyFields(false)
        const updated = await updateSetting();

        if (!updated) return;
        await createMultiple(item?.id)
        const deletedIds = getDeletedFileIds(item.eventSettingFileDto, photoPickerFile.listFiles);
        await handleDeleteFiles(deletedIds)
        setModified(true);
    }

    const getData = () => {
        dispatch(GetEventSetting()).then(({ payload }) => {
            const { data, code } = payload;

            if (data.success) {
                const config = data.data;
                setItem((prev) => ({ ...prev, ...config }));

                setPhotoPickerFile((prev) => ({
                    ...prev,
                    listFiles: config?.eventSettingFileDto?.map((e) => PHOTO_PICKER_MODEL.toSet(e))
                }));
            } else {
                errorMsg(code);
            }
        });
    }

    const updateSetting = async () => {
        const body = item.toUpdate()

         return dispatch(UpdateEventSetting(body))
            .then(({ payload: { data, status } }) => {
                if (status >= 200 && status < 300 && data?.success) {
                    toast.success(t('has_been_updated_successfully'));
                    return true;
                } else {
                    const code = data?.code;
                    errorMsg(code);
                    return false;
                }
            })
    }

    useEffect(() => {
        getData()
    }, []);


    useEffect(() => {
        if (modified) {
            getData()
            setModified(false)
        }
    }, [modified])


    const handleDeleteFiles = async(ids) => {
        if (!ids.length) return

        dispatch(DeleteFileEventSetting(ids)).then(({ payload: { data: { data, code, success } } }) => {
            if (success) {
            } else errorMsg(code)
        })
    }

    // Return the necessary values and functions for the component to use
    return {
        photoPickerFile,
        setPhotoPickerFile,
        item,
        emptyFields,
        handleChange,
        handleUpdate,
        disabled,
        isBookingWindowEnabled,
        isAttendeesLimitEnabled
    };
};

export default ManageApp;
